import { NavLink, useLocation, useSearchParams } from 'react-router-dom';
import { Home, Zap, Package, Calendar, Image, Star, MessageCircle } from 'lucide-react';
import clsx from 'clsx';
import { BrandLogo } from '@/components/BrandLogo';
import { useAuth } from '@/lib/auth';

export interface PortalCounts {
  praAprovar: number;
  entregas: number;
  eventos: number;
  galeria: number;
  oportunidades: number;
}

interface Props { counts: PortalCounts }

export function PortalSidebar({ counts }: Props) {
  const { user } = useAuth();
  const loc = useLocation();
  const [params] = useSearchParams();
  const asSuffix = params.get('as') ? `?as=${params.get('as')}` : '';
  const items: { to: string; label: string; icon: typeof Home; count?: number; urgent?: boolean; opportunity?: boolean; section?: string }[] = [
    { to: '/portal',              label: 'Início',           icon: Home,         section: 'AÇÕES' },
    { to: '/portal/aprovar',      label: 'Pra aprovar',      icon: Zap,          count: counts.praAprovar, urgent: counts.praAprovar > 0 },
    { to: '/portal/entregas',     label: 'Entregas',         icon: Package,      count: counts.entregas, section: 'MEU PLANO' },
    { to: '/portal/eventos',      label: 'Eventos',          icon: Calendar,     count: counts.eventos },
    { to: '/portal/galeria',      label: 'Galeria',          icon: Image,        count: counts.galeria },
    { to: '/portal/oportunidades',label: 'Oportunidades',    icon: Star,         count: counts.oportunidades, opportunity: true, section: 'APEX' },
    { to: '/portal/contato',      label: 'Falar com a Apex', icon: MessageCircle },
  ];

  return (
    <aside className="w-[232px] bg-[#001B3A] text-white flex flex-col fixed inset-y-0 left-0 p-3 overflow-y-auto">
      <div className="flex items-center gap-2 pb-3 mb-2 border-b border-white/10">
        <BrandLogo size={28} />
        <div className="font-bold text-[13px] tracking-tight">
          Research
          <div className="text-[10px] text-blue-300 font-medium">{user?.nome ?? 'Portal'}</div>
        </div>
      </div>

      <nav className="mt-2 space-y-0.5">
        {items.map((it, i) => {
          const Icon = it.icon;
          const active = it.to === '/portal'
            ? loc.pathname === '/portal'
            : loc.pathname === it.to || loc.pathname.startsWith(it.to + '/');
          return (
            <div key={it.to}>
              {it.section && (
                <div className={clsx(
                  'text-[9px] font-bold uppercase tracking-wider px-2.5 pb-1.5',
                  i === 0 ? 'pt-1' : 'pt-3.5',
                  'text-white/40',
                )}>
                  {it.section}
                </div>
              )}
              <NavLink
                to={`${it.to}${asSuffix}`}
                end={it.to === '/portal'}
                className={clsx(
                  'flex items-center gap-2.5 w-full px-2.5 py-2 rounded-md text-xs font-semibold transition-colors',
                  active
                    ? 'bg-gradient-to-r from-blue-500/20 to-blue-500/5 text-blue-300 border-l-2 border-blue-400'
                    : 'text-white/75 hover:bg-white/5 hover:text-white',
                  it.opportunity && 'text-orange-300',
                )}
              >
                <Icon className="w-4 h-4 opacity-80" />
                <span className="flex-1">{it.label}</span>
                {it.count !== undefined && it.count > 0 && (
                  <span className={clsx(
                    'text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[22px] text-center',
                    it.urgent ? 'bg-red-500 text-white' :
                    it.opportunity ? 'bg-orange-500 text-white' :
                    'bg-white/12 text-white/85',
                  )}>
                    {it.count}
                  </span>
                )}
              </NavLink>
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
